import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { suppressContact } from '../db/baseql/suppression-ops.js';
import { toast } from '../toast.js';

export const OptOutPage = stateful(() => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (emailToSuppress?: string) => {
    const target = (emailToSuppress ?? email).trim();
    if (!target || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      await suppressContact(target)();
      setDone(true);
      toast.success('You will no longer receive emails from us.');
    } catch (e: any) {
      setError(e?.message ?? 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') void handleSubmit();
  };

  // ── One-click mode: if ?email= is in the URL, auto-submit on load ──
  const urlEmail =
    typeof window !== 'undefined' ? (new URLSearchParams(window.location.search).get('email') ?? '') : '';

  useEffect(() => {
    if (urlEmail) void handleSubmit(urlEmail);
  }, []);

  // ── Confirmation screen (shown after successful suppression) ──
  if (done) {
    return html`
      <div class="ndi-gradient flex min-h-screen items-center justify-center">
        <div class="card bg-base-100 w-full max-w-sm shadow-xl">
          <div class="card-body text-center">
            <div class="mb-4 flex flex-col items-center">
              <span class="icon-[material-symbols--mark-email-read] text-success text-5xl"></span>
            </div>
            <h2 class="card-title justify-center text-lg font-semibold">You're unsubscribed</h2>
            <p class="text-base-content/70 text-sm">
              You will no longer receive emails from us. If you change your mind, you can contact us anytime.
            </p>
          </div>
        </div>
      </div>
    `;
  }

  // ── Loading screen (shown during auto-submit from email link) ──
  if (urlEmail && loading) {
    return html`
      <div class="ndi-gradient flex min-h-screen items-center justify-center">
        <div class="card bg-base-100 w-full max-w-sm shadow-xl">
          <div class="card-body text-center">
            <span class="loading loading-spinner loading-lg text-primary mx-auto"></span>
            <p class="text-base-content/70 mt-4 text-sm">Processing your request...</p>
          </div>
        </div>
      </div>
    `;
  }

  // ── Error screen (auto-submit failed) ──
  if (urlEmail && error && !loading) {
    return html`
      <div class="ndi-gradient flex min-h-screen items-center justify-center">
        <div class="card bg-base-100 w-full max-w-sm shadow-xl">
          <div class="card-body text-center">
            <div class="mb-4 flex flex-col items-center">
              <span class="icon-[material-symbols--error-outline] text-error text-5xl"></span>
            </div>
            <h2 class="card-title justify-center text-lg font-semibold">Something went wrong</h2>
            <p class="text-base-content/70 text-sm">${error}</p>
            <button class="btn btn-primary mt-4 w-full" @click=${() => handleSubmit(urlEmail)}>Try again</button>
          </div>
        </div>
      </div>
    `;
  }

  // ── Manual form (direct visit without ?email= param) ──
  return html`
    <div class="ndi-gradient flex min-h-screen items-center justify-center">
      <div class="card bg-base-100 w-full max-w-sm shadow-xl">
        <div class="card-body">
          <div class="mb-2 flex flex-col items-center">
            <span class="ndi-logo-text text-4xl">NDI</span>
            <span class="text-base-content/50 mt-1 text-xs font-medium tracking-widest uppercase"
              >Email Preferences</span
            >
          </div>
          <h2 class="card-title mb-2 justify-center text-lg font-semibold">Stop Contact</h2>
          <p class="text-base-content/60 mb-4 text-center text-sm">
            Enter your email address below and we will stop contacting you permanently.
          </p>

          ${error ? html`<div class="alert alert-error text-sm">${error}</div>` : nothing}

          <div class="form-control">
            <label class="label"><span class="label-text">Email Address</span></label>
            <input
              type="email"
              class="input input-bordered w-full"
              placeholder="you@example.com"
              .value=${email}
              @input=${(e: InputEvent) => setEmail((e.target as HTMLInputElement).value)}
              @keydown=${handleKeydown}
              ?disabled=${loading}
            />
          </div>

          <button
            class="btn btn-primary ${loading ? 'loading' : ''} mt-4 w-full"
            @click=${() => handleSubmit()}
            ?disabled=${loading}
          >
            ${loading ? 'Processing...' : 'Stop Contact'}
          </button>
        </div>
      </div>
    </div>
  `;
});
